import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, Debt, DebtType, Event, Notification, Payment, FundraisingCampaign, CampaignDonation, Service } from '../types';
import { convertToHebrewDate } from '../data/torahData';

// API Settings - will be loaded from database
let API_SETTINGS = {
  whatsappApiUrl: '/whatsapp/hook',
  smsApiUrl: '/sms/http/send-message',
  emailApiUrl: '/email/send',
  smsUsername: 'admin',
  smsPassword: 'eran4ever',
  iCountApiKey: '',
  iCountApiUrl: '',
  // Email SMTP Settings
  smtpHost: 'smtp.gmail.com',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  smtpFromEmail: '',
  smtpFromName: 'זכרון דליה'
};

// Load API settings from database
export const loadApiSettings = async (): Promise<void> => {
  try {
    console.log('🔄 Loading API settings from database...');
    const settingsDoc = await getDoc(doc(db, 'settings', 'api'));
    
    if (settingsDoc.exists()) {
      console.log('✅ Found existing API settings');
      const data = settingsDoc.data();
      API_SETTINGS = {
        whatsappApiUrl: '/whatsapp/hook', // Always use local proxy
        smsApiUrl: '/sms/http/send-message', // Always use local proxy
        emailApiUrl: '/email/send', // Always use local proxy
        smsUsername: data.smsUsername || API_SETTINGS.smsUsername,
        smsPassword: data.smsPassword || API_SETTINGS.smsPassword,
        iCountApiKey: data.iCountApiKey || API_SETTINGS.iCountApiKey,
        iCountApiUrl: data.iCountApiUrl || API_SETTINGS.iCountApiUrl,
        // Email SMTP Settings
        smtpHost: data.smtpHost || API_SETTINGS.smtpHost,
        smtpPort: data.smtpPort || API_SETTINGS.smtpPort,
        smtpSecure: data.smtpSecure !== undefined ? data.smtpSecure : API_SETTINGS.smtpSecure,
        smtpUser: data.smtpUser || API_SETTINGS.smtpUser,
        smtpPassword: data.smtpPassword || API_SETTINGS.smtpPassword,
        smtpFromEmail: data.smtpFromEmail || API_SETTINGS.smtpFromEmail,
        smtpFromName: data.smtpFromName || API_SETTINGS.smtpFromName
      };
      console.log('📋 Loaded settings:', API_SETTINGS);
    } else {
      // Create default settings if they don't exist
      console.log('📝 Creating default API settings in database...');
      const defaultSettings = {
        whatsappApiUrl: '/whatsapp/hook',
        smsApiUrl: '/sms/http/send-message',
        emailApiUrl: '/email/send',
        smsUsername: API_SETTINGS.smsUsername,
        smsPassword: API_SETTINGS.smsPassword,
        iCountApiKey: API_SETTINGS.iCountApiKey,
        iCountApiUrl: API_SETTINGS.iCountApiUrl,
        // Email SMTP Settings
        smtpHost: API_SETTINGS.smtpHost,
        smtpPort: API_SETTINGS.smtpPort,
        smtpSecure: API_SETTINGS.smtpSecure,
        smtpUser: API_SETTINGS.smtpUser,
        smtpPassword: API_SETTINGS.smtpPassword,
        smtpFromEmail: API_SETTINGS.smtpFromEmail,
        smtpFromName: API_SETTINGS.smtpFromName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await setDoc(doc(db, 'settings', 'api'), defaultSettings);
      console.log('✅ Default API settings created successfully');
      console.log('📋 Created settings:', defaultSettings);
    }
  } catch (error) {
    console.error('❌ Error loading/creating API settings:', error);
    console.log('⚠️  Using default settings from code');
  }
};

// Update existing users with email field
export const updateUsersWithEmailField = async (): Promise<void> => {
  try {
    console.log('🔄 Updating users with email field...');
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    let updatedCount = 0;
    
    for (const userDoc of querySnapshot.docs) {
      const userData = userDoc.data();
      
      // Check if user needs email field update
      if (!userData.hasOwnProperty('email') || !userData.notificationPreferences?.hasOwnProperty('email')) {
        console.log(`📝 Updating user: ${userData.name} ${userData.lastName}`);
        
        await updateDoc(doc(db, 'users', userDoc.id), {
          email: userData.email || '', // Add email field if missing
          notificationPreferences: {
            whatsapp: userData.notificationPreferences?.whatsapp ?? true,
            sms: userData.notificationPreferences?.sms ?? true,
            calls: userData.notificationPreferences?.calls ?? false,
            email: userData.notificationPreferences?.email ?? false // Add email preference
          }
        });
        
        updatedCount++;
      }
    }
    
    if (updatedCount > 0) {
      console.log(`✅ Updated ${updatedCount} users with email field`);
    } else {
      console.log('✅ All users already have email field');
    }
  } catch (error) {
    console.error('❌ Error updating users:', error);
  }
};

// Save API settings to database
export const saveApiSettings = async (settings: typeof API_SETTINGS): Promise<void> => {
  try {
    await updateDoc(doc(db, 'settings', 'api'), settings);
    API_SETTINGS = settings;
  } catch (error) {
    console.error('Error saving API settings:', error);
    throw error;
  }
};

// Get current API settings
export const getApiSettings = () => API_SETTINGS;

// SMS Service
export const sendSMS = async (phone: string, message: string): Promise<boolean> => {
  try {
    const fullPhone = phone.startsWith('972') ? phone : `972${phone.replace('0', '')}`;
    const settings = getApiSettings();
    const url = `${settings.smsApiUrl}?username=${settings.smsUsername}&password=${settings.smsPassword}&to=${fullPhone}&message-type=sms.automatic&message=${encodeURIComponent(message)}`;
    
    console.log('Sending SMS to:', url);
    const response = await fetch(url);
    console.log('SMS response:', response.status, response.statusText);
    return response.ok;
  } catch (error) {
    console.error('Error sending SMS:', error);
    return false;
  }
};

// WhatsApp Service
export const sendWhatsApp = async (phone: string, message: string): Promise<boolean> => {
  try {
    const fullPhone = phone.startsWith('972') ? phone : `972${phone.replace('0', '')}`;
    const settings = getApiSettings();
    const url = `${settings.whatsappApiUrl}?body=${encodeURIComponent(message)}&phone=${fullPhone}`;
    
    console.log('Sending WhatsApp to:', url);
    const response = await fetch(url);
    console.log('WhatsApp response:', response.status, response.statusText);
    return response.ok;
  } catch (error) {
    console.error('Error sending WhatsApp:', error);
    return false;
  }
};

export const sendEmail = async (email: string, subject: string, message: string): Promise<boolean> => {
  try {
    const settings = getApiSettings();
    const url = `${settings.emailApiUrl || '/email/send'}`;
    
    console.log('Sending Email to:', email);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: email,
        subject: subject,
        message: message,
        smtpSettings: {
          host: settings.smtpHost,
          port: settings.smtpPort,
          secure: settings.smtpSecure,
          auth: {
            user: settings.smtpUser,
            pass: settings.smtpPassword
          },
          from: `"${settings.smtpFromName}" <${settings.smtpFromEmail}>`
        }
      })
    });
    console.log('Email response:', response.status, response.statusText);
    return response.ok;
  } catch (error) {
    console.error('Error sending Email:', error);
    return false;
  }
};

// User Management
export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'users'), {
    ...userData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
};

export const getUserByPhone = async (phone: string): Promise<User | null> => {
  const q = query(collection(db, 'users'), where('phone', '==', phone));
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) return null;
  
  const doc = querySnapshot.docs[0];
  return { id: doc.id, ...doc.data() } as User;
};

export const getAllUsers = async (): Promise<User[]> => {
  const querySnapshot = await getDocs(collection(db, 'users'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
};

export const updateUser = async (userId: string, updates: Partial<User>): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

// Debt Management
export const createDebt = async (debtData: Omit<Debt, 'id' | 'createdAt' | 'payments' | 'remainingAmount'>): Promise<string> => {
  const remainingAmount = debtData.amount - debtData.paidAmount;
  
  const docRef = await addDoc(collection(db, 'debts'), {
    ...debtData,
    remainingAmount,
    payments: [],
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

// Create Torah-based debt with enhanced data structure
export const createTorahDebt = async (debtData: Omit<Debt, 'id' | 'createdAt' | 'payments' | 'remainingAmount'>): Promise<string> => {
  const remainingAmount = debtData.amount - debtData.paidAmount;
  
  // Find or create Torah debt type
  let torahDebtTypeId = '';
  const debtTypesSnapshot = await getDocs(collection(db, 'debtTypes'));
  
  debtTypesSnapshot.forEach(doc => {
    if (doc.data().name === 'חוב תורני') {
      torahDebtTypeId = doc.id;
    }
  });
  
  // If Torah debt type doesn't exist, create it
  if (!torahDebtTypeId) {
    const newDebtTypeRef = await addDoc(collection(db, 'debtTypes'), {
      name: 'חוב תורני',
      description: 'חובות תורניים - שבתות וחגים',
      createdAt: Timestamp.now()
    });
    torahDebtTypeId = newDebtTypeRef.id;
  }
  
  // Clean up undefined values before saving
  const cleanDebtData = Object.fromEntries(
    Object.entries(debtData).filter(([_, value]) => value !== undefined)
  );
  
  const docRef = await addDoc(collection(db, 'debts'), {
    ...cleanDebtData,
    debtTypeId: torahDebtTypeId, // Set the Torah debt type
    remainingAmount,
    payments: [],
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getDebtsByMember = async (memberId: string): Promise<Debt[]> => {
  const q = query(collection(db, 'debts'), where('memberId', '==', memberId));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Debt));
};

export const getAllDebts = async (): Promise<Debt[]> => {
  const querySnapshot = await getDocs(collection(db, 'debts'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Debt));
};

export const addPayment = async (debtId: string, payment: Omit<Payment, 'id' | 'paymentDate'>): Promise<void> => {
  const debtRef = doc(db, 'debts', debtId);
  const debtDoc = await getDoc(debtRef);
  
  if (!debtDoc.exists()) throw new Error('Debt not found');
  
  const debtData = debtDoc.data() as Debt;
  const newPayment: Payment = {
    ...payment,
    id: Date.now().toString(),
    paymentDate: new Date()
  };
  
  const updatedPayments = [...debtData.payments, newPayment];
  const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
  const remainingAmount = debtData.amount - totalPaid;
  
  await updateDoc(debtRef, {
    payments: updatedPayments,
    paidAmount: totalPaid,
    remainingAmount,
    isPaid: remainingAmount <= 0
  });
};

// Debt Types
export const createDebtType = async (debtTypeData: Omit<DebtType, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'debtTypes'), {
    ...debtTypeData,
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getAllDebtTypes = async (): Promise<DebtType[]> => {
  const querySnapshot = await getDocs(collection(db, 'debtTypes'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DebtType));
};

export const updateDebtType = async (debtTypeId: string, debtTypeData: Partial<DebtType>): Promise<void> => {
  const debtTypeRef = doc(db, 'debtTypes', debtTypeId);
  await updateDoc(debtTypeRef, {
    ...debtTypeData,
    updatedAt: Timestamp.now()
  });
};

export const deleteDebtType = async (debtTypeId: string): Promise<void> => {
  const debtTypeRef = doc(db, 'debtTypes', debtTypeId);
  await deleteDoc(debtTypeRef);
};

// Events
export const createEvent = async (eventData: Omit<Event, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'events'), {
    ...eventData,
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getUpcomingEvents = async (limitCount: number = 10): Promise<Event[]> => {
  const q = query(
    collection(db, 'events'),
    where('eventDate', '>=', new Date()),
    orderBy('eventDate'),
    limit(limitCount)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Event));
};

// Notifications
export const createNotification = async (notificationData: Omit<Notification, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'notifications'), {
    ...notificationData,
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getRecentNotifications = async (limitCount: number = 10): Promise<Notification[]> => {
  const q = query(
    collection(db, 'notifications'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notification));
};


// Delete User
export const deleteUser = async (userId: string): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  await deleteDoc(userRef);
};

// Update Debt
export const updateDebt = async (debtId: string, updates: Partial<Debt>): Promise<void> => {
  const debtRef = doc(db, 'debts', debtId);
  
  // If amount is being updated, recalculate remainingAmount
  if (updates.amount !== undefined) {
    const debtDoc = await getDoc(debtRef);
    if (debtDoc.exists()) {
      const currentDebt = debtDoc.data() as Debt;
      const paidAmount = currentDebt.paidAmount || 0;
      const newRemainingAmount = updates.amount - paidAmount;
      
      await updateDoc(debtRef, {
        ...updates,
        remainingAmount: newRemainingAmount,
        updatedAt: Timestamp.now()
      });
    } else {
      await updateDoc(debtRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    }
  } else {
    await updateDoc(debtRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }
};

// Delete Debt
export const deleteDebt = async (debtId: string): Promise<void> => {
  const debtRef = doc(db, 'debts', debtId);
  await deleteDoc(debtRef);
};

// Update Event
export const updateEvent = async (eventId: string, updates: Partial<Event>): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

// Services Management
export const createService = async (serviceData: Omit<Service, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'services'), {
    ...serviceData,
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getAllServices = async (): Promise<Service[]> => {
  const querySnapshot = await getDocs(collection(db, 'services'));
  return querySnapshot.docs.map(doc => ({ 
    id: doc.id, 
    ...doc.data() 
  } as Service));
};

export const updateService = async (serviceId: string, updates: Partial<Service>): Promise<void> => {
  const serviceRef = doc(db, 'services', serviceId);
  await updateDoc(serviceRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

export const deleteService = async (serviceId: string): Promise<void> => {
  const serviceRef = doc(db, 'services', serviceId);
  await deleteDoc(serviceRef);
};

// Fix existing Torah debts that have prayerTypeId: 'other'
export const fixTorahDebtsPrayerTypes = async (): Promise<void> => {
  try {
    console.log('Starting to fix Torah debts prayer types...');
    
    // Get all Torah debts
    const debtsSnapshot = await getDocs(collection(db, 'debts'));
    const batch = writeBatch(db);
    let updateCount = 0;
    
    debtsSnapshot.forEach(doc => {
      const debt = doc.data() as Debt;
      
      // Only update Torah debts that have prayerTypeId: 'other'
      if (debt.debtCategory === 'shabbat' || debt.debtCategory === 'holiday') {
        if (debt.prayerTypeId === 'other') {
          // Set a default prayer type based on debt category
          const newPrayerTypeId = debt.debtCategory === 'shabbat' ? 'shacharit_regular' : 'shacharit_regular';
          
          batch.update(doc.ref, {
            prayerTypeId: newPrayerTypeId,
            updatedAt: Timestamp.now()
          });
          updateCount++;
          console.log(`Updating debt ${doc.id} from 'other' to '${newPrayerTypeId}'`);
        }
      }
    });
    
    if (updateCount > 0) {
      await batch.commit();
      console.log(`Successfully updated ${updateCount} Torah debts`);
    } else {
      console.log('No Torah debts need updating');
    }
  } catch (error) {
    console.error('Error fixing Torah debts prayer types:', error);
  }
};

// Delete Event
export const deleteEvent = async (eventId: string): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);
  await deleteDoc(eventRef);
};

// Dashboard Stats
export const getDashboardStats = async (): Promise<any> => {
  const [users, debts, events] = await Promise.all([
    getAllUsers(),
    getAllDebts(),
    getUpcomingEvents()
  ]);

  const totalMembers = users.length;
  const totalDebts = debts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
  const totalPaid = debts.reduce((sum, debt) => sum + (debt.paidAmount || 0), 0);
  const totalUnpaid = totalDebts - totalPaid;
  const upcomingEvents = events.length;

  const topDebtors = debts
    .filter(debt => (debt.remainingAmount || 0) > 0)
    .reduce((acc, debt) => {
      const existing = acc.find(item => item.memberId === debt.memberId);
      if (existing) {
        existing.totalDebt += (debt.remainingAmount || 0);
      } else {
        const member = users.find(u => u.id === debt.memberId);
        if (member) {
          acc.push({
            memberId: debt.memberId,
            memberName: `${member.name} ${member.lastName}`,
            totalDebt: (debt.remainingAmount || 0)
          });
        } else {
          // Handle deleted members
          acc.push({
            memberId: debt.memberId,
            memberName: 'מתפלל נמחק',
            totalDebt: (debt.remainingAmount || 0)
          });
        }
      }
      return acc;
    }, [] as Array<{ memberId: string; memberName: string; totalDebt: number }>)
    .sort((a, b) => b.totalDebt - a.totalDebt)
    .slice(0, 10);

  return {
    totalMembers,
    totalDebts,
    totalPaid,
    totalUnpaid,
    upcomingEvents,
    topDebtors
  };
};

// Campaign Management Functions
export const getAllCampaigns = async (): Promise<FundraisingCampaign[]> => {
  try {
    const campaignsRef = collection(db, 'campaigns');
    const q = query(campaignsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      startDate: doc.data().startDate,
      endDate: doc.data().endDate
    })) as FundraisingCampaign[];
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const getCampaignById = async (campaignId: string): Promise<FundraisingCampaign | null> => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (campaignSnap.exists()) {
      return {
        id: campaignSnap.id,
        ...campaignSnap.data(),
        createdAt: campaignSnap.data()?.createdAt,
        startDate: campaignSnap.data()?.startDate,
        endDate: campaignSnap.data()?.endDate
      } as FundraisingCampaign;
    }
    return null;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

export const createCampaign = async (campaignData: Omit<FundraisingCampaign, 'id' | 'createdAt' | 'currentAmount'>): Promise<string> => {
  try {
    const campaignsRef = collection(db, 'campaigns');
    const newCampaign = {
      ...campaignData,
      currentAmount: 0,
      createdAt: Timestamp.now()
    };
    
    const docRef = await addDoc(campaignsRef, newCampaign);
    return docRef.id;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

export const updateCampaign = async (campaignId: string, campaignData: Partial<FundraisingCampaign>): Promise<void> => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    await updateDoc(campaignRef, campaignData);
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

export const deleteCampaign = async (campaignId: string): Promise<void> => {
  try {
    const campaignRef = doc(db, 'campaigns', campaignId);
    await deleteDoc(campaignRef);
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

// Campaign Donations Functions
export const getCampaignDonations = async (campaignId: string): Promise<CampaignDonation[]> => {
  try {
    const donationsRef = collection(db, 'campaignDonations');
    // Avoid Firestore composite index by fetching filtered and sorting client-side
    const q = query(donationsRef, where('campaignId', '==', campaignId));
    const querySnapshot = await getDocs(q);
    
    const list = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      donationDate: doc.data().donationDate
    })) as CampaignDonation[];
    
    // Sort newest first safely handling Timestamp/Date/string
    return list.sort((a, b) => {
      try {
        const da = (a.donationDate && typeof a.donationDate === 'object' && 'toDate' in a.donationDate)
          ? a.donationDate.toDate() : (a.donationDate instanceof Date ? a.donationDate : new Date(a.donationDate));
        const dbb = (b.donationDate && typeof b.donationDate === 'object' && 'toDate' in b.donationDate)
          ? b.donationDate.toDate() : (b.donationDate instanceof Date ? b.donationDate : new Date(b.donationDate));
        return (dbb.getTime() - da.getTime());
      } catch {
        return 0;
      }
    });
  } catch (error) {
    console.error('Error fetching campaign donations:', error);
    throw error;
  }
};

export const addCampaignDonation = async (donationData: Omit<CampaignDonation, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const donationsRef = collection(db, 'campaignDonations');
    const newDonation = {
      ...donationData,
      createdAt: Timestamp.now()
    };
    
    const docRef = await addDoc(donationsRef, newDonation);
    
    // Update campaign current amount
    const campaignRef = doc(db, 'campaigns', donationData.campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (campaignSnap.exists()) {
      const currentAmount = campaignSnap.data()?.currentAmount || 0;
      await updateDoc(campaignRef, {
        currentAmount: currentAmount + donationData.amount
      });
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding campaign donation:', error);
    throw error;
  }
};

export const updateCampaignDonation = async (donationId: string, donationData: Partial<CampaignDonation>): Promise<void> => {
  try {
    const donationRef = doc(db, 'campaignDonations', donationId);
    await updateDoc(donationRef, donationData);
  } catch (error) {
    console.error('Error updating campaign donation:', error);
    throw error;
  }
};

export const deleteCampaignDonation = async (donationId: string, campaignId: string, amount: number): Promise<void> => {
  try {
    const donationRef = doc(db, 'campaignDonations', donationId);
    await deleteDoc(donationRef);
    
    // Update campaign current amount
    const campaignRef = doc(db, 'campaigns', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (campaignSnap.exists()) {
      const currentAmount = campaignSnap.data()?.currentAmount || 0;
      await updateDoc(campaignRef, {
        currentAmount: Math.max(0, currentAmount - amount)
      });
    }
  } catch (error) {
    console.error('Error deleting campaign donation:', error);
    throw error;
  }
};

// פונקציה לעדכון תאריכים עבריים בחובות קיימים
export const updateHebrewDates = async (): Promise<void> => {
  try {
    const debtsSnapshot = await getDocs(collection(db, 'debts'));
    const batch = writeBatch(db);
    
    debtsSnapshot.forEach(doc => {
      const debtData = doc.data();
      
      // עדכן רק חובות תורניים שיש להם תאריך עברי עם מספרים
      if (debtData.debtCategory && debtData.hebrewDate && /\d/.test(debtData.hebrewDate)) {
        try {
          // נסה להמיר את התאריך הקיים
          const dateMatch = debtData.hebrewDate.match(/(\d+)\s+(\w+)\s+התש"(\d+)/);
          if (dateMatch) {
            const day = parseInt(dateMatch[1]);
            const monthName = dateMatch[2];
            const year = parseInt('20' + dateMatch[3]);
            
            // המרת שם חודש למספר
            const hebrewMonths = ['', 'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר', 'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול'];
            const month = hebrewMonths.indexOf(monthName);
            
            if (month > 0) {
              const date = new Date(year, month - 1, day);
              const newHebrewDate = convertToHebrewDate(date);
              
              batch.update(doc.ref, {
                hebrewDate: newHebrewDate
              });
            }
          }
        } catch (error) {
          console.log('Error updating Hebrew date for debt:', doc.id, error);
        }
      }
    });
    
    await batch.commit();
    console.log('תאריכים עבריים עודכנו בהצלחה');
  } catch (error) {
    console.error('שגיאה בעדכון תאריכים עבריים:', error);
    throw error;
  }
};


// פונקציה לעדכון חובות קיימים למערכת תורנית
export const migrateExistingDebts = async (): Promise<void> => {
  try {
    // קבל את כל החובות הקיימים
    const debtsSnapshot = await getDocs(collection(db, 'debts'));
    const batch = writeBatch(db);
    
    // קבל את סוג החוב "חוב ישן"
    const debtTypesSnapshot = await getDocs(collection(db, 'debtTypes'));
    let oldDebtTypeId = '';
    
    debtTypesSnapshot.forEach(doc => {
      if (doc.data().name === 'חוב ישן') {
        oldDebtTypeId = doc.id;
      }
    });
    
    // אם לא קיים, צור אותו
    if (!oldDebtTypeId) {
      const newDebtTypeRef = doc(collection(db, 'debtTypes'));
      batch.set(newDebtTypeRef, {
        name: 'חוב ישן',
        description: 'חובות ישנים שהועברו למערכת תורנית',
        createdAt: Timestamp.now()
      });
      oldDebtTypeId = newDebtTypeRef.id;
    }
    
    // עדכן כל חוב קיים
    debtsSnapshot.forEach(doc => {
      const debtData = doc.data();
      
      // בדוק אם זה חוב תורני (יש לו debtCategory)
      if (!debtData.debtCategory) {
        // קבל תאריך יצירה של החוב
        const createdAt = debtData.createdAt;
        let hebrewDate = 'כללי';
        let gregorianDate = '2025-01-01';
        
        // אם יש תאריך יצירה, נסה להמיר אותו
        if (createdAt) {
          try {
            let date: Date;
            if (createdAt.toDate) {
              date = createdAt.toDate();
            } else if (createdAt instanceof Date) {
              date = createdAt;
            } else {
              date = new Date(createdAt);
            }
            
            // המרה לתאריך עברי
            hebrewDate = convertToHebrewDate(date);
            gregorianDate = date.toISOString().split('T')[0];
          } catch (error) {
            console.log('Error converting date:', error);
            // השאר ברירת מחדל
          }
        }
        
        batch.update(doc.ref, {
          debtCategory: 'shabbat',
          torahPortionId: 'general-5786', // פרשה כללית
          prayerTypeId: 'shacharit_regular', // שחרית רגיל
          serviceId: 'aliyah_1', // עליה ראשונה
          hebrewDate: hebrewDate,
          gregorianDate: gregorianDate,
          debtTypeId: oldDebtTypeId // עדכן לסוג החוב החדש
        });
      }
    });
    
    await batch.commit();
    console.log('חובות קיימים הועברו בהצלחה למערכת תורנית');
  } catch (error) {
    console.error('שגיאה בהעברת חובות קיימים:', error);
    throw error;
  }
};
